import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { isApiErrorMessage } from 'src/api/utils/is-api-error-message';
import { inviteAdmin } from 'src/features/administrators/tenant/api/lib/admins';
import { adminsKeys } from 'src/features/administrators/tenant/api/query-keys/admins';
import { UserRole } from 'src/types/enum/user-role';
import { ApiError } from 'src/types/interfaces/api-error';
import { delayedCallbackExecution } from 'src/utils/delayed-callback-execution';
import { ApiErrorMessage } from '../../api/types/api-error-message';
import { administratorsApiErrorsTranslations } from '../../constants/api-errors-translations';

export const useInviteAdmin = (onSuccess?: () => void) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      email,
      phoneNumber,
      role,
    }: {
      email: string;
      phoneNumber: string | undefined;
      role: UserRole;
    }) => inviteAdmin(email, phoneNumber, role),
    onSuccess: () => {
      delayedCallbackExecution(1000, () => {
        queryClient.invalidateQueries({ queryKey: adminsKeys.all });
      });

      onSuccess?.();
      toast.success(t('organizationDetails:admins.inviteSuccess'));
    },
    onError: (error: ApiError) => {
      const errorMessage = error?.response?.data?.message;

      if (
        isApiErrorMessage<ApiErrorMessage>(
          errorMessage,
          administratorsApiErrorsTranslations,
        )
      ) {
        toast.error(t(administratorsApiErrorsTranslations[errorMessage]));

        return;
      }

      toast.error(errorMessage || t('common:errorMessages.somethingWentWrong'));
    },
  });
};
